const FUNNEL_CONCEPT_QUERY_PARAM = "concept";

export const FUNNEL_CONCEPTS = {
  agents: {
    key: "agents-angle",
    label: "Agents angle",
    queryValues: ["agents", "agent", "agents-angle", "default"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Keep your agents running",
    heroTitleAccent: "while you step away",
    heroSubtitle: "Awakeo keeps long-running Claude, Codex, and local agent sessions alive when your Mac screen sleeps or you step away from your desk.",
  },
  battery: {
    key: "battery-angle",
    label: "Battery angle",
    queryValues: ["battery", "battery-aware", "battery-angle"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Stay awake.",
    heroTitleAccent: "Protect battery.",
    heroSubtitle: "Battery-aware thresholds stop Awakeo before your laptop drains, with calm session continuity and guardrails.",
  },
  displaySleep: {
    key: "display-sleep-angle",
    label: "Display sleep angle",
    queryValues: ["display-sleep", "display", "screen-sleep", "display-sleep-angle"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Display can sleep.",
    heroTitleAccent: "Your session won't.",
    heroSubtitle: "Let the screen go dark while builds, scripts, and agent runs keep moving in the background.",
  },
  shortcut: {
    key: "shortcut-angle",
    label: "Shortcut angle",
    queryValues: ["shortcut", "shortcuts", "shortcut-angle"],
    heroTitleLayout: "flow" as const,
    heroTitleMain: "One shortcut.",
    heroTitleAccent: "Session continuity.",
    heroSubtitle: "Toggle Awakeo from anywhere on your Mac. Menu bar calm, global shortcut fast.",
  },
  macSleep: {
    key: "mac-sleep-angle",
    label: "Mac sleep angle",
    queryValues: ["mac-sleep", "sleep", "mac-sleep-angle"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Mac can sleep.",
    heroTitleAccent: "Your session won't.",
    heroSubtitle: "Close the lid, walk away, or let the Mac idle — Awakeo keeps agent sessions alive through system sleep so you come back to a finished run, not a dead one.",
  },
  claudeCheck: {
    key: "claude-check-angle",
    label: "Claude check angle",
    queryValues: ["claude-check", "claude", "claude-check-angle", "checking"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Still checking if Claude finished?",
    heroTitleAccent: "",
    heroSubtitle: "Awakeo detects live Claude Code sessions, keeps them running while your display sleeps, and notifies you the moment the run is done.",
  },
  // Concepts below are message-matched to the 2026-07-01 Meta ad set (8 concepts).
  // Primary queryValue == the ad-folder slug so ad destination URLs map 1:1.
  // See outputs/awakeo-collateral/ads/landing-concept-map.md.
  deadRun: {
    key: "dead-run-angle",
    label: "Dead run angle",
    queryValues: ["dead-run", "dead-run-angle", "deadrun", "lost-run"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Your Mac slept.",
    heroTitleAccent: "Your run doesn't have to.",
    heroSubtitle: "Awakeo keeps long agent runs, builds, and scripts alive when your Mac would normally sleep — so you never come back to a killed session and lost compute.",
  },
  halfOpen: {
    key: "half-open-angle",
    label: "Half-open angle",
    queryValues: ["half-open", "half-open-walk", "half-open-angle", "halfopen", "lid"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Stop carrying it half-open.",
    heroTitleAccent: "Just close the lid.",
    heroSubtitle: "No more wedging the lid open or hauling a half-open Mac around. Awakeo keeps your agent sessions running with the lid shut and the display asleep.",
  },
  touchGrass: {
    key: "touch-grass-angle",
    label: "Touch grass angle",
    queryValues: ["touch-grass", "touch-grass-angle", "touchgrass", "freedom"],
    heroTitleLayout: "flow" as const,
    heroTitleMain: "Touch grass.",
    heroTitleAccent: "Your agent's got this.",
    heroSubtitle: "Step away and live your life — Awakeo keeps Claude, Codex, and local agent runs shipping while your Mac is idle or asleep.",
  },
  hallOfShame: {
    key: "hall-of-shame-angle",
    label: "Hall of shame angle",
    queryValues: ["hall-of-shame", "hall-of-shame-angle", "caffeinate", "hacks", "jiggler"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Retire the mouse jiggler.",
    heroTitleAccent: "And the caffeinate ritual.",
    heroSubtitle: "No terminal hacks, looping videos, or half-open lids. One menu-bar toggle keeps your Mac awake only while your agents are actually working.",
  },
  agentAware: {
    key: "agent-aware-angle",
    label: "Agent-aware angle",
    queryValues: ["agent-aware", "agent-aware-angle", "agentaware", "smart"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Awake while it works.",
    heroTitleAccent: "Asleep when it's done.",
    heroSubtitle: "Awakeo detects live Claude Code, Codex, and Cursor sessions, keeps your Mac awake while they run, and lets it sleep the moment they finish — no wasted battery.",
  },
  overnight: {
    key: "overnight-angle",
    label: "Overnight angle",
    queryValues: ["overnight", "overnight-angle", "night", "night-shift"],
    heroTitleLayout: "flow" as const,
    heroTitleMain: "Go to sleep.",
    heroTitleAccent: "Wake up shipped.",
    heroSubtitle: "Kick off a long run at night and let it finish while you sleep. Battery-aware guardrails keep it safe until morning.",
  },
  socialProof: {
    key: "social-proof-angle",
    label: "Social proof angle",
    queryValues: ["social-proof", "social-proof-angle", "socialproof", "reviews"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Developers don't go back.",
    heroTitleAccent: "Neither will you.",
    heroSubtitle: "Join the developers running Claude, Codex, and Cursor overnight without babysitting their Macs — local-first, battery-safe, one-time purchase.",
  },
  theClose: {
    key: "the-close-angle",
    label: "The close angle",
    queryValues: ["the-close", "the-close-angle", "offer", "bofu", "price"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "One-time $29.",
    heroTitleAccent: "Never lose a run.",
    heroSubtitle: "Awakeo keeps your Claude, Codex, and Cursor sessions alive when you step away — local-first, so your code never leaves your Mac. One-time purchase, no subscription.",
  },
} as const;

export type FunnelConceptKey = (typeof FUNNEL_CONCEPTS)[keyof typeof FUNNEL_CONCEPTS]["key"];
export type FunnelConcept = (typeof FUNNEL_CONCEPTS)[keyof typeof FUNNEL_CONCEPTS];

const conceptEntries = Object.values(FUNNEL_CONCEPTS);
const conceptByQueryValue = new Map(
  conceptEntries.flatMap((concept) => concept.queryValues.map((value) => [value.toLowerCase(), concept] as const)),
);

export const DEFAULT_FUNNEL_CONCEPT = FUNNEL_CONCEPTS.agents;

export const isFunnelConceptKey = (value: string): value is FunnelConceptKey =>
  conceptEntries.some((concept) => concept.key === value);

export const getFunnelConceptByKey = (key: FunnelConceptKey): FunnelConcept =>
  conceptEntries.find((concept) => concept.key === key) ?? DEFAULT_FUNNEL_CONCEPT;

export const getFunnelConceptFromLocation = (locationLike: Pick<Location, "href"> | URL): FunnelConcept | null => {
  const url = locationLike instanceof URL ? locationLike : new URL(locationLike.href);
  const raw = url.searchParams.get(FUNNEL_CONCEPT_QUERY_PARAM)?.trim().toLowerCase();
  if (!raw) return null;
  return conceptByQueryValue.get(raw) ?? null;
};

export const getStoredOrDefaultFunnelConcept = (storedConceptKey: string | null | undefined): FunnelConcept =>
  storedConceptKey && isFunnelConceptKey(storedConceptKey)
    ? getFunnelConceptByKey(storedConceptKey)
    : DEFAULT_FUNNEL_CONCEPT;
